import { useMutation, useQueryClient } from '@mint-lib/query-client';
import { MediaPlan } from '@mint-sdk/api-media-plan';

import { useColumnsView } from '../../hooks/useColumnsView.js';

const applyColumnsOrder = (
  items: MediaPlan.Column[],
  visibleIds: string[],
): MediaPlan.Column[] => {
  const itemsById = new Map(items.map((col) => [col.id, col]));

  const visibleItems = visibleIds
    .map((id) => itemsById.get(id))
    .filter((col): col is MediaPlan.Column => !!col)
    .map((col) => ({ ...col, isHidden: false }));
  const hiddenItems = items
    .filter((col) => !visibleIds.includes(col.id))
    .map((col) => ({ ...col, isHidden: true }));

  return [...visibleItems, ...hiddenItems];
};

export const useColumnsMutationApi = () => {
  const { viewType, relatedEntityId } = useColumnsView();
  const queryClient = useQueryClient();

  const getColumnsQueryKey = () =>
    MediaPlan.getColumnsListByViewQueryOptions({
      urlParams: { viewType, relatedEntityId },
    }).queryKey;

  return useMutation({
    mutationFn: async (columns: string[]) =>
      MediaPlan.updateColumnsByView({
        urlParams: { viewType, relatedEntityId },
        data: { columns },
      }),
    onMutate: async (columns: string[]) => {
      if (!relatedEntityId) return;

      const queryKey = getColumnsQueryKey();

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<MediaPlan.ColumnsResponse>(queryKey);

      queryClient.setQueryData<MediaPlan.ColumnsResponse>(
        queryKey,
        (oldColumns) => {
          if (!oldColumns?.data?.items) return oldColumns;

          return {
            ...oldColumns,
            data: {
              ...oldColumns.data,
              items: applyColumnsOrder(oldColumns.data.items, columns),
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_, __, context) => {
      if (!context?.previousData) {
        return;
      }

      queryClient.setQueryData<MediaPlan.ColumnsResponse>(
        getColumnsQueryKey(),
        context.previousData,
      );
    },
  });
};
